import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { SiteStatusBadge } from '@/components/StatusBadge';
import { isoDate } from '@/lib/format';
import { useSiteOverview } from '@/lib/queries';
import { useSite } from '@/lib/useSite';

/**
 * 현장 개요.
 *
 * <p>계약·기성은 「내 것」이지만 개요는 <b>현장의 것</b>이라, 배정된 현장이면 협력사도 감리도
 * 같은 것을 본다 — 서버의 경계도 `vendor_id` 가 아니라 `site_id` 하나다.
 *
 * <p><b>도급금액 칸이 없다.</b> 응답에 담기지 않아서가 아니라, 발주처와 본사 사이의 계약이라
 * 협력사가 자기 계약 금액과 견주면 곧 원가율이 드러나기 때문이다. 없는 값의 자리를 세우지 않는다.
 */
export function OverviewPage() {
  const { siteId, siteName } = useSite();
  const overview = useSiteOverview(siteId);
  const row = overview.data ?? null;

  return (
    <>
      <PageHeader title="현장 개요" subtitle={siteName} />
      <QueryState
        pending={overview.isPending}
        error={overview.isError}
        empty={row === null}
        emptyText="현장 정보가 없습니다."
      >
        {row !== null && (
          <div className="flex flex-col gap-2">
            <div className="card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-ui-lg font-semibold">{row.name}</div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {row.siteCategory}
                  </div>
                </div>
                <SiteStatusBadge status={row.status} />
              </div>
            </div>

            <dl className="card flex flex-col gap-2 text-ui-xs">
              <Field label="발주처" value={row.clientName} />
              <Field label="용도" value={row.facilityType} />
              <Field label="규모" value={row.scale} />
              <Field label="주소" value={row.siteAddress} />
            </dl>

            <dl className="card flex flex-col gap-2 text-ui-xs">
              <Field label="계약기간" value={period(row.contractStartDate, row.contractEndDate)} />
              <Field label="실착공" value={row.actualStartDate === null ? null : isoDate(row.actualStartDate)} />
              <Field
                label="준공예정"
                value={row.scheduledCompletionDate === null ? null : isoDate(row.scheduledCompletionDate)}
              />
            </dl>
          </div>
        )}
      </QueryState>
    </>
  );
}

/** 기간 한 줄. 한쪽만 있으면 그쪽만 적는다 — `- ~ 2026-11-30` 은 읽는 사람을 멈추게 한다. */
function period(from: string | null, to: string | null): string | null {
  if (from === null && to === null) return null;
  if (from === null) return `~ ${isoDate(to)}`;
  if (to === null) return `${isoDate(from)} ~`;
  return `${isoDate(from)} ~ ${isoDate(to)}`;
}

/**
 * 개요 한 칸.
 *
 * <p>비어 있어도 <b>줄을 지우지 않는다.</b> 현장 등록이 진행되며 채워지는 값이라, 칸이 사라지면
 * 「이 현장은 원래 주소가 없는 현장」처럼 읽힌다.
 */
function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0" style={{ color: 'var(--text-secondary)' }}>{label}</dt>
      <dd className="min-w-0 text-right" style={value === null ? { color: 'var(--text-muted)' } : undefined}>
        {value ?? '-'}
      </dd>
    </div>
  );
}
